/**
 * Verification Item Table Component
 *
 * Displays verification items grouped by unit in a table format
 * matching the Mazak 設計検証項目一覧 layout.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import {
  VerificationSession,
  VerificationUnit,
  VerificationItem,
} from '../../types/verification';
import {
  classificationKeys,
  methodKeys,
  getLocalizedClassificationLabel,
  getLocalizedMethodLabel,
} from '../../utils/verificationLabels';

interface VerificationItemTableProps {
  session: VerificationSession;
  onAddUnit?: () => void;
  onEditUnit?: (unit: VerificationUnit) => void;
  onDeleteUnit?: (unitId: string) => void;
  onAddItem?: (unitId: string) => void;
  onEditItem?: (item: VerificationItem) => void;
  onDeleteItem?: (itemId: string) => void;
}

// ==============================================================================
// Styles (must be defined before component)
// ==============================================================================

// Sticky column positions (only No + Unit are sticky to save horizontal space)
const COL0_WIDTH = 32;  // No
const COL1_WIDTH = 72;  // Unit

const stickyBase = {
  position: 'sticky' as const,
  zIndex: 3,
};

const stickyCol0Style = {
  ...stickyBase,
  left: 0,
  minWidth: COL0_WIDTH,
  width: COL0_WIDTH,
};

const stickyCol1Style = {
  ...stickyBase,
  left: COL0_WIDTH,
  minWidth: COL1_WIDTH,
  width: COL1_WIDTH,
  borderRight: '2px solid rgba(0,0,0,0.2)',
};

const verticalHeaderCell = {
  minWidth: 28,
  width: 28,
  maxWidth: 28,
  textAlign: 'center' as const,
  px: 0,
  py: 0.5,
  overflow: 'hidden',
};

const verticalTextStyle = {
  writingMode: 'vertical-rl' as const,
  textOrientation: 'mixed' as const,
  fontSize: '0.6rem',
  lineHeight: 1.2,
  whiteSpace: 'nowrap' as const,
  mx: 'auto',
  height: 64,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const headerStyle = {
  fontWeight: 'bold',
  fontSize: '0.7rem',
  backgroundColor: '#424242',
  color: '#fff',
  borderRight: '1px solid rgba(255,255,255,0.2)',
  whiteSpace: 'nowrap' as const,
  position: 'sticky' as const,
  top: 0,
  zIndex: 2,
};

const classHeaderStyle = {
  backgroundColor: '#e0f7fa',
  color: '#000',
};

const methodHeaderStyle = {
  backgroundColor: '#e8f5e9',
  color: '#000',
};

const unitCellStyle = {
  backgroundColor: '#ccffcc',
  fontWeight: 'bold',
  borderRight: '1px solid rgba(0,0,0,0.12)',
  fontSize: '0.75rem',
};

const specCellStyle = {
  backgroundColor: '#ffffcc',
  borderRight: '1px solid rgba(0,0,0,0.12)',
  fontSize: '0.75rem',
};

const dataCellStyle = {
  borderRight: '1px solid rgba(0,0,0,0.06)',
  fontSize: '0.75rem',
  py: 0.5,
  px: 0.5,
  backgroundColor: '#fff',
};

const VerificationItemTable: React.FC<VerificationItemTableProps> = ({
  session,
  onAddUnit,
  onEditUnit,
  onDeleteUnit,
  onAddItem,
  onEditItem,
  onDeleteItem,
}) => {
  const { t } = useTranslation();
  const [unitMenuAnchor, setUnitMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuUnitId, setMenuUnitId] = useState<string | null>(null);

  const handleUnitMenuOpen = (event: React.MouseEvent<HTMLElement>, unitId: string) => {
    event.stopPropagation();
    setUnitMenuAnchor(event.currentTarget);
    setMenuUnitId(unitId);
  };

  const handleUnitMenuClose = () => {
    setUnitMenuAnchor(null);
    setMenuUnitId(null);
  };

  const totalItems = session.units.reduce((sum, u) => sum + u.items.length, 0);

  return (
    <Box>
      {/* Table header info */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {session.units.length} {t('verification.units')} / {totalItems} {t('verification.items')}
        </Typography>
        {onAddUnit && (
          <Tooltip title={t('verification.addUnit')}>
            <IconButton size="small" color="primary" onClick={onAddUnit}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 280px)', overflowX: 'auto' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
          <TableHead>
            {/* Row 1: Group headers */}
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{ ...headerStyle, ...stickyCol0Style, zIndex: 4 }}
              >
                No
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{ ...headerStyle, ...stickyCol1Style, zIndex: 4 }}
              >
                {t('verification.table.unit')}
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{ ...headerStyle, minWidth: 100 }}
              >
                {t('verification.table.specification')}
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{ ...headerStyle, minWidth: 120 }}
              >
                {t('verification.table.itemName')}
              </TableCell>
              <TableCell
                colSpan={classificationKeys.length}
                sx={{ ...headerStyle, ...classHeaderStyle, textAlign: 'center' }}
              >
                {t('verification.table.classification')}
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{ ...headerStyle, minWidth: 160 }}
              >
                {t('verification.table.standardTarget')}
              </TableCell>
              <TableCell
                colSpan={methodKeys.length}
                sx={{ ...headerStyle, ...methodHeaderStyle, textAlign: 'center' }}
              >
                {t('verification.table.verificationMethod')}
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{ ...headerStyle, minWidth: 40, textAlign: 'center' }}
              >
                {t('verification.table.review')}
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{ ...headerStyle, minWidth: 36, width: 36 }}
              >
                &nbsp;
              </TableCell>
            </TableRow>
            {/* Row 2: Sub-headers */}
            <TableRow>
              {classificationKeys.map((key) => (
                <TableCell
                  key={key}
                  sx={{
                    ...headerStyle,
                    ...classHeaderStyle,
                    ...verticalHeaderCell,
                  }}
                >
                  <Box sx={verticalTextStyle}>
                    {getLocalizedClassificationLabel(t, key)}
                  </Box>
                </TableCell>
              ))}
              {methodKeys.map((key) => (
                <TableCell
                  key={key}
                  sx={{
                    ...headerStyle,
                    ...methodHeaderStyle,
                    ...verticalHeaderCell,
                  }}
                >
                  <Box sx={verticalTextStyle}>
                    {getLocalizedMethodLabel(t, key)}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {session.units.length === 0 && (
              <TableRow>
                <TableCell colSpan={4 + classificationKeys.length + 1 + methodKeys.length + 2} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {t('verification.noUnits')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {session.units.map((unit) => {
              const items = unit.items;
              if (items.length === 0) {
                // Empty unit - show one row for the unit
                return (
                  <TableRow key={unit.id}>
                    <TableCell sx={{ ...unitCellStyle, ...stickyCol0Style, textAlign: 'center' }}>
                      {unit.unitNumber}
                    </TableCell>
                    <TableCell sx={{ ...unitCellStyle, ...stickyCol1Style }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                          {unit.unitName}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleUnitMenuOpen(e, unit.id)}
                          sx={{ ml: 'auto' }}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell
                      colSpan={2 + classificationKeys.length + 1 + methodKeys.length + 2}
                      sx={{ textAlign: 'center' }}
                    >
                      {onAddItem && (
                        <Chip
                          icon={<AddIcon />}
                          label={t('verification.addItem')}
                          variant="outlined"
                          size="small"
                          onClick={() => onAddItem(unit.id)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              }

              // Group items by specification for merging
              const specGroups: { spec: string; items: VerificationItem[] }[] = [];
              for (const item of items) {
                const lastGroup = specGroups[specGroups.length - 1];
                if (lastGroup && lastGroup.spec === item.specification) {
                  lastGroup.items.push(item);
                } else {
                  specGroups.push({ spec: item.specification, items: [item] });
                }
              }

              return specGroups.map((group, groupIdx) =>
                group.items.map((item, itemIdx) => {
                  const isFirstInGroup = itemIdx === 0;
                  const isFirstInUnit = groupIdx === 0 && itemIdx === 0;

                  return (
                    <TableRow key={item.id} hover>
                      {/* Unit number (merged) */}
                      {isFirstInUnit && (
                        <TableCell
                          rowSpan={items.length}
                          sx={{ ...unitCellStyle, ...stickyCol0Style, textAlign: 'center', verticalAlign: 'top' }}
                        >
                          {unit.unitNumber}
                        </TableCell>
                      )}
                      {/* Unit name (merged) */}
                      {isFirstInUnit && (
                        <TableCell
                          rowSpan={items.length}
                          sx={{ ...unitCellStyle, ...stickyCol1Style, verticalAlign: 'top' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ flex: 1, fontSize: '0.75rem' }}>
                              {unit.unitName}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleUnitMenuOpen(e, unit.id)}
                              >
                                <MoreIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </TableCell>
                      )}
                      {/* Specification (merged per group) */}
                      {isFirstInGroup && (
                        <TableCell
                          rowSpan={group.items.length}
                          sx={{ ...specCellStyle, verticalAlign: 'top' }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {group.spec}
                          </Typography>
                        </TableCell>
                      )}
                      {/* Item name */}
                      <TableCell sx={dataCellStyle}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {item.itemName}
                        </Typography>
                      </TableCell>
                      {/* Classifications */}
                      {classificationKeys.map((key) => (
                        <TableCell key={key} sx={{ ...dataCellStyle, textAlign: 'center', px: 0, minWidth: 28, maxWidth: 28 }}>
                          {item.classifications.includes(key) && (
                            <Typography sx={{ fontSize: '0.75rem' }}>◎</Typography>
                          )}
                        </TableCell>
                      ))}
                      {/* Standard/target */}
                      <TableCell sx={dataCellStyle}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                          {item.standardTarget}
                        </Typography>
                      </TableCell>
                      {/* Verification methods */}
                      {methodKeys.map((key) => (
                        <TableCell key={key} sx={{ ...dataCellStyle, textAlign: 'center', px: 0, minWidth: 28, maxWidth: 28 }}>
                          {item.verificationMethodsPrimary.includes(key) && (
                            <Typography sx={{ fontSize: '0.75rem' }}>■</Typography>
                          )}
                          {item.verificationMethodsSecondary.includes(key) && (
                            <Typography sx={{ fontSize: '0.75rem' }}>□</Typography>
                          )}
                        </TableCell>
                      ))}
                      {/* Review judgment */}
                      <TableCell sx={{ ...dataCellStyle, textAlign: 'center' }}>
                        {item.reviewJudgment === 'required' && (
                          <Chip label={t('verification.reviewRequired')} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {item.reviewJudgment === 'not_required' && (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{t('verification.reviewNotRequired')}</Typography>
                        )}
                      </TableCell>
                      {/* Actions */}
                      <TableCell sx={{ ...dataCellStyle, px: 0 }}>
                        <Box sx={{ display: 'flex', gap: 0 }}>
                          {item.sourceDrbfmSessionId && (
                            <Tooltip title={t('verification.drbfmLinkTooltip', 'DRBFM分析から生成')}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => window.open(`/drbfm?session=${item.sourceDrbfmSessionId}`, '_blank')}
                              >
                                <LinkIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onEditItem && (
                            <IconButton size="small" onClick={() => onEditItem(item)}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                          {onDeleteItem && (
                            <IconButton size="small" onClick={() => onDeleteItem(item.id)} color="error">
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Unit context menu */}
      <Menu anchorEl={unitMenuAnchor} open={Boolean(unitMenuAnchor)} onClose={handleUnitMenuClose}>
        {onAddItem && (
          <MenuItem onClick={() => {
            if (menuUnitId) onAddItem(menuUnitId);
            handleUnitMenuClose();
          }}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            {t('verification.addItem')}
          </MenuItem>
        )}
        {onEditUnit && (
          <MenuItem onClick={() => {
            const unit = session.units.find((u) => u.id === menuUnitId);
            if (unit) onEditUnit(unit);
            handleUnitMenuClose();
          }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            {t('verification.editUnit')}
          </MenuItem>
        )}
        {onDeleteUnit && (
          <MenuItem onClick={() => {
            if (menuUnitId) onDeleteUnit(menuUnitId);
            handleUnitMenuClose();
          }} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {t('verification.deleteUnit')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default VerificationItemTable;
